import { Component, inject, Input, OnChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StaffService } from '@extensions/assignment/services/staff.service';
import { WorkService } from '@extensions/assignment/services/work.service';
import { DateRangeComponent, IDateRangeInput } from '@shared/dialogs/date-range/date-range.component';
import { EvGroupSelectComponent } from '@shared/dialogs/ev-group-select/ev-group-select.component';
import { ProjectUploadComponent, IProjectUploadData } from '@shared/dialogs/project-upload/project-upload.component';
import { ActivitiesService } from '@shared/services/activities.service';
import { AdminService } from '@shared/services/admin.service';
import { ProjectService } from '@shared/services/project.service';
import { EPermissionLevel } from '@static/models/permissions';
import { IProject, IProjectListData } from '@static/models/project';
import { DatetimeDatePipe } from '@static/pipes/datetime-date.pipe';
import { AuthService } from '@static/services/auth.service';
import { ObservableService } from '@static/services/observable.service';
import moment from 'moment';

@Component({
  selector: 'app-info-portfolio',
  imports: [
    MatCardModule,
    MatIconModule,
    MatTooltipModule,
    MatMenuModule,
    MatButtonModule,
    ReactiveFormsModule,
    DatetimeDatePipe,
  ],
  templateUrl: './info-portfolio.component.html',
  styleUrl: './info-portfolio.component.scss',
})
export class InfoPortfolioComponent implements OnChanges {
  private _observableService: ObservableService = inject(ObservableService);
  private _projectService: ProjectService = inject(ProjectService);
  private _staffService: StaffService = inject(StaffService);
  private _dialog: MatDialog = inject(MatDialog);
  private _authService: AuthService = inject(AuthService);
  private _activitiesService: ActivitiesService = inject(ActivitiesService);
  private _workService: WorkService = inject(WorkService);
  private _adminService: AdminService = inject(AdminService);

  @Input() projectInfo!: IProject[];
  @Input() portfolioTitle!: string;
  @Input() accessLevel!: number;
  @Input() extAssignment!: boolean;
  @Input() extExpense!: boolean;
  @Input() extChanges!: boolean;
  @Input() isSiteAdmin!: boolean;
  @Input() isGlobalAdmin!: boolean;
  @Input() lockedState!: boolean;
  @Input() isArchived!: boolean;
  @Input() canLock!: boolean;

  protected earliestStart?: string;
  protected latestEnd?: string;
  protected earliestDataDate?: string;
  protected totalTasks?: number;
  protected allLocked?: boolean;
  protected lockCount?: number;

  protected PERMISSION_LEVEL = EPermissionLevel;

  protected projectUploadControl: FormControl;
  protected staffUploadControl: FormControl;

  constructor() {
    this.projectUploadControl = new FormControl();
    this.staffUploadControl = new FormControl();
  }

  ngOnChanges(): void {
    if (this.projectInfo.length) {
      this.earliestStart = this._projectService.getProjectStartDate(this._projectService.currentId);
      this.latestEnd = this._projectService.getProjectEndDate(this._projectService.currentId);
      this.earliestDataDate = this._projectService.getProjectDataDate(this._projectService.currentId);
      this.totalTasks = this.projectInfo.reduce((sum, p) => sum + (p.task_count ? +p.task_count : 0), 0);
      this._calculateLocks();
    }
  }

  /**
   * Upload a staffing file which details contractors and supervisors in a known format
   *
   * @param event file upload event
   */
  public staffFileUpload(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    if (!files || files === null || !files.length) return;

    this._staffService.staffFileUpload(files[0]);
    this.staffUploadControl.reset();
  }

  /**
   * Upload a project file for processing
   *
   * @param event
   * @returns
   */
  public projectFileUpload(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    if (!files) return;

    // Extract and build form
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    formData.append('user', this._authService.email ?? '');

    // Post and wait for upload
    const dialog = this._dialog.open(ProjectUploadComponent, {
      disableClose: true,
      data: {
        formData,
        dataCallback: this._appendProjects.bind(this)
      } as IProjectUploadData<IProjectListData>
    });
    dialog.afterClosed().subscribe(() => {
      this.projectUploadControl.reset();
    });
  }

  /**
   * Calculate if all the projects are locked
   */
  private _calculateLocks(): void {
    const lockInfo = this.projectInfo.map(p => p.lock);
    this.allLocked = lockInfo.every(Boolean);
    this.lockCount = lockInfo.filter(v => !!v).length;
  }


  /**
   * Append the new projects to the project list
   *
   * @param res projects to add
   */
  private _appendProjects(res: IProjectListData): void {
    this._projectService.appendProjects(res.projectInfo);
    this._projectService.appendProjectSettings(res.projectSettings);

    this._projectService.getCurrentInfo().then(() => {
      this._activitiesService.getWBSTaskStructure();
    });
  }

  /**
   * Toggle the project lock
   */
  public toggleProjectLock(): void {
    if (this.projectInfo) {
      const newState = this.allLocked ? 0 : 1;
      this._projectService.toggleLock(this._projectService.currentId!, newState).then(() => {
        this.projectInfo.forEach(p => p.lock = newState);
        this._calculateLocks();
      });
    }
  }

  /**
   * Trigger the export for activities with updated percentage complete
   */
  public exportChangedPercentComplete(): void {
    this._activitiesService.exportChangedPercentComplete();
  }

  /**
   * Trigger the export for work assignments in the UGL format
   */
  public exportAssignedWork(): void {
    const endDate = moment.utc(this._projectService.getProjectDataDate(this._projectService.currentId))
      .add(this._projectService.getProjectUpdateFrequency(this._projectService.currentId) - 1, 'd');
    const confirm = this._dialog.open(DateRangeComponent, { data: {
      title: `Select the date range for the Assigned Work Export`,
      showGroups: true,
      start: this._projectService.getProjectDataDate(this._projectService.currentId),
      end: endDate,
      periodDuration: this._projectService.getProjectUpdateFrequency(this._projectService.currentId),
      periodKey: 'd',
    } as IDateRangeInput });
    this._observableService.subscribe(
      confirm.afterClosed(),
      (data?: [number, number, string]) => {
        if (data === undefined) return;
        this._workService.exportAssignedWork(data[0], data[1], data[2]);
      }
    )
  }

  /**
   * Export the budget hours
   */
  public exportBudgetHours(): void {
    this._activitiesService.exportBudgetHours();
  }

  /**
   * Trigger the export for all activities in the current filter configuration
   */
  public exportWBS(): void {
    this._activitiesService.exportWBSTaskStructure();
  }

  /**
   * Trigger the export for activities with no updated percentage complete but have work assigned
   */
  public exportNoPercentageUpdate(): void {
    this._activitiesService.exportNoPercentageUpdate();
  }

  /**
   * Export the user access list
   */
  public exportUserAccess(): void {
    this._adminService.exportProjectUserAccessList();
  }

  /**
   * Export list of comments
   */
  public exportComments(): void {
    this._activitiesService.exportComments();
  }

  /**
   * Export the list of work assignments with no underlying task
   */
  public exportMissingTasks(): void {
    this._activitiesService.exportMissingTasks();
  }

  /**
   * Export the earned value
   */
  public exportEarnedValue(): void {
    const confirm = this._dialog.open(EvGroupSelectComponent);
    this._observableService.subscribe(
      confirm.afterClosed(),
      (groupId?: number) => {
        if (groupId === undefined) return;
        this._activitiesService.exportEarnedValue(groupId);
      }
    )
  }
}
